import type { LocationPositionCardProps } from './types';

/**
 * 위치 정보를 표시하는 재사용 가능한 카드 컴포넌트
 * 색상과 라벨을 커스터마이징하여 다양한 위치 타입에 사용 가능
 */
export default function LocationPositionCard({
  color,
  label,
  position,
  mapKey,
  isMapExpanded,
  mapRef,
  onToggleMap,
  badge,
  detailToggle,
  showKakaoMapLink = false,
}: Readonly<LocationPositionCardProps>) {
  // 색상별 클래스 매핑
  const colorClasses = {
    blue: {
      border: 'border-blue-500',
      dot: 'bg-blue-500',
      badge: 'bg-blue-100 text-blue-800',
      detailButton: 'text-blue-600 hover:text-blue-800 bg-blue-50',
      kakaoLink: 'text-blue-600 hover:text-blue-800 bg-blue-50',
      button: {
        expanded: 'text-blue-700 bg-blue-100 hover:bg-blue-200',
        collapsed: 'text-gray-600 bg-gray-100 hover:bg-gray-200',
      },
    },
    green: {
      border: 'border-green-500',
      dot: 'bg-green-500',
      badge: 'bg-green-100 text-green-800',
      detailButton: 'text-green-600 hover:text-green-800 bg-green-50',
      kakaoLink: 'text-green-600 hover:text-green-800 bg-green-50',
      button: {
        expanded: 'text-green-700 bg-green-100 hover:bg-green-200',
        collapsed: 'text-gray-600 bg-gray-100 hover:bg-gray-200',
      },
    },
    amber: {
      border: 'border-amber-500',
      dot: 'bg-amber-500',
      badge: 'bg-amber-100 text-amber-800',
      detailButton: 'text-amber-600 hover:text-amber-800 bg-amber-50',
      kakaoLink: 'text-amber-600 hover:text-amber-800 bg-amber-50',
      button: {
        expanded: 'text-amber-700 bg-amber-100 hover:bg-amber-200',
        collapsed: 'text-gray-600 bg-gray-100 hover:bg-gray-200',
      },
    },
    red: {
      border: 'border-red-500',
      dot: 'bg-red-500',
      badge: 'bg-red-100 text-red-800',
      detailButton: 'text-red-600 hover:text-red-800 bg-red-50',
      kakaoLink: 'text-red-600 hover:text-red-800 bg-red-50',
      button: {
        expanded: 'text-red-700 bg-red-100 hover:bg-red-200',
        collapsed: 'text-gray-600 bg-gray-100 hover:bg-gray-200',
      },
    },
    purple: {
      border: 'border-purple-500',
      dot: 'bg-purple-500',
      badge: 'bg-purple-100 text-purple-800',
      detailButton: 'text-purple-600 hover:text-purple-800 bg-purple-50',
      kakaoLink: 'text-purple-600 hover:text-purple-800 bg-purple-50',
      button: {
        expanded: 'text-purple-700 bg-purple-100 hover:bg-purple-200',
        collapsed: 'text-gray-600 bg-gray-100 hover:bg-gray-200',
      },
    },
    indigo: {
      border: 'border-indigo-500',
      dot: 'bg-indigo-500',
      badge: 'bg-indigo-100 text-indigo-800',
      detailButton: 'text-indigo-600 hover:text-indigo-800 bg-indigo-50',
      kakaoLink: 'text-indigo-600 hover:text-indigo-800 bg-indigo-50',
      button: {
        expanded: 'text-indigo-700 bg-indigo-100 hover:bg-indigo-200',
        collapsed: 'text-gray-600 bg-gray-100 hover:bg-gray-200',
      },
    },
  };

  const colors = colorClasses[color];

  return (
    <div
      className={`bg-white rounded-lg border-l-4 ${colors.border} overflow-hidden`}
    >
      <div className="p-4">
        <div className="flex items-center mb-2">
          <div className={`w-4 h-4 ${colors.dot} rounded-full mr-2`}></div>
          <span className="text-sm font-semibold text-gray-900">{label}</span>
          {badge && (
            <div
              className={`ml-2 px-2 py-1 ${colors.badge} text-xs rounded-full`}
            >
              {badge}
            </div>
          )}
        </div>
        <p className="text-sm font-medium text-gray-800 mb-1">
          {position.name}
        </p>
        <p className="text-sm text-gray-600 mb-3">{position.address}</p>

        <div className="flex items-center gap-2">
          {detailToggle && (
            <button
              onClick={detailToggle.onToggle}
              className={`text-xs ${colors.detailButton} px-2 py-1 rounded-full transition-colors`}
            >
              <i
                className={`ri-${detailToggle.isExpanded ? 'eye-off' : 'information'}-line mr-1`}
              ></i>
              {detailToggle.isExpanded ? '상세정보 숨김' : '상세정보 보기'}
            </button>
          )}
          <button
            onClick={() => onToggleMap(mapKey)}
            className={`text-xs px-2 py-1 rounded-full transition-colors ${
              isMapExpanded ? colors.button.expanded : colors.button.collapsed
            }`}
          >
            <i
              className={`ri-${isMapExpanded ? 'eye-off' : 'map'}-line mr-1`}
            ></i>
            {isMapExpanded ? '지도 숨김' : '지도 보기'}
          </button>
        </div>

        {detailToggle?.isExpanded && (
          <div className="pt-3 mt-3 border-t border-gray-100">
            <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
              <div>위도: {position.latitude.toFixed(6)}°</div>
              <div>경도: {position.longitude.toFixed(6)}°</div>
            </div>
          </div>
        )}
      </div>
      {isMapExpanded && mapRef && (
        <div className="border-t bg-gray-50 p-4">
          <div className="bg-white rounded-lg overflow-hidden shadow-sm border">
            {(!position.latitude || !position.longitude) && (
              <div className="text-center text-gray-500 text-sm w-[400px] h-[400px] flex items-center justify-center">
                위도, 경도 정보가 없습니다. 위도 경도 정보를 입력해주세요.
              </div>
            )}
            {!!position.latitude && !!position.longitude && (
              <div
                ref={mapRef}
                className="w-full bg-gray-100"
                style={{ height: '400px', minHeight: '400px' }}
              />
            )}
          </div>
          {showKakaoMapLink && (
            <div className="mt-3 text-center">
              <a
                href={`https://map.kakao.com/link/map/${position.name},${position.latitude},${position.longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-2 ${colors.kakaoLink} text-sm px-3 py-2 rounded-lg transition-colors`}
              >
                <i className="ri-external-link-line"></i>
                <span>카카오맵에서 보기</span>
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
